// src/context/AuthContext.jsx
import React, { createContext, useContext, useReducer, useEffect } from 'react';
import { authService } from '../features/auth/services/authService';
import { tokenService } from '../features/auth/services/tokenService';
import { DashboardCache } from '../features/dashboard/utils/dashboardCache';
import { staffAPI } from '../features/staff/services/staff';

// Auth state shape
const initialState = {
  isAuthenticated: false,
  isLoading: true,
  user: null,
  token: null,
  error: null,
};

// Auth actions
const AuthActions = {
  SET_LOADING: 'SET_LOADING',
  LOGIN_SUCCESS: 'LOGIN_SUCCESS',
  LOGIN_FAILURE: 'LOGIN_FAILURE',
  LOGOUT: 'LOGOUT',
  SET_USER: 'SET_USER',
  CLEAR_ERROR: 'CLEAR_ERROR',
  TOKEN_REFRESH_SUCCESS: 'TOKEN_REFRESH_SUCCESS',
  TOKEN_REFRESH_FAILURE: 'TOKEN_REFRESH_FAILURE',
};

// Auth reducer
function authReducer(state, action) {
  switch (action.type) {
    case AuthActions.SET_LOADING:
      return { ...state, isLoading: action.payload };

    case AuthActions.LOGIN_SUCCESS:
      return {
        ...state,
        isAuthenticated: true,
        isLoading: false,
        user: action.payload.user,
        token: action.payload.token,
        error: null,
      };

    case AuthActions.LOGIN_FAILURE:
      return {
        ...state,
        isAuthenticated: false,
        isLoading: false,
        user: null,
        token: null,
        error: action.payload,
      };

    case AuthActions.LOGOUT:
      return {
        ...initialState,
        isLoading: false,
      };

    case AuthActions.SET_USER:
      return {
        ...state,
        user: action.payload,
      };

    case AuthActions.CLEAR_ERROR:
      return {
        ...state,
        error: null,
      };

    case AuthActions.TOKEN_REFRESH_SUCCESS:
      return {
        ...state,
        token: action.payload.token,
        error: null,
      };

    case AuthActions.TOKEN_REFRESH_FAILURE:
      return {
        ...state,
        isAuthenticated: false,
        user: null,
        token: null,
        error: action.payload,
      };

    default:
      return state;
  }
}

// Create context
export const AuthContext = createContext();

// Auth provider component
export function AuthProvider({ children }) {
  const [state, dispatch] = useReducer(authReducer, initialState);

  // Initialize auth state on mount
  useEffect(() => {
    initializeAuth();
  }, []);

  // Set up token refresh timer when authenticated
  useEffect(() => {
    let refreshTimer;
    
    if (state.isAuthenticated && state.token) {
      // NOTE: Refreshtimer is disabled, the user will refresh their tokens themselves
      // refreshTimer = setupTokenRefresh();
    }

    // Cleanup timer on unmount or when auth state changes
    return () => {
      if (refreshTimer) {
        clearTimeout(refreshTimer);
      }
    };
  }, [state.isAuthenticated, state.token]);

  const initializeAuth = async () => {
    try {
      dispatch({ type: AuthActions.SET_LOADING, payload: true });
      
      const token = tokenService.getToken();

      if (!token || tokenService.isTokenExpired()) {
        console.log('AuthContext: No valid token found, logging out');
        dispatch({ type: AuthActions.LOGOUT });
        return;
      }

      // Verify token and get user info
      console.log('AuthContext: Verifying token and fetching user data');
      const user = await authService.getCurrentUser();

      dispatch({
        type: AuthActions.LOGIN_SUCCESS,
        payload: { user, token },
      });

      console.log('AuthContext: Auth initialization successful');
    } catch (error) {
      console.error('AuthContext: Auth initialization failed:', error);
      dispatch({ type: AuthActions.LOGOUT });
    }
  };

  const setupTokenRefresh = () => {
    const timeUntilExpiration = tokenService.getTimeUntilExpiration();
    
    // Don't set up refresh if token is already expired or expires very soon
    if (timeUntilExpiration <= 60000) { // Less than 1 minute
      console.log('AuthContext: Token expires too soon, not setting up refresh');
      return null;
    }

    // Refresh 5 minutes before expiration, but at least 1 minute from now
    const refreshTime = Math.max(timeUntilExpiration - 5 * 60 * 1000, 60000);
    
    console.log('AuthContext: Setting up token refresh in', refreshTime / 1000, 'seconds');

    const timer = setTimeout(async () => {
      try {
        console.log('AuthContext: Attempting automatic token refresh');
        const refreshResult = await authService.refreshToken();
        
        if (refreshResult && refreshResult.token) {
          dispatch({
            type: AuthActions.TOKEN_REFRESH_SUCCESS,
            payload: { token: refreshResult.token },
          });
          console.log('AuthContext: Token refreshed successfully');
        } else {
          throw new Error('Token refresh returned no token');
        }
      } catch (error) {
        console.error('AuthContext: Automatic token refresh failed:', error);
        dispatch({
          type: AuthActions.TOKEN_REFRESH_FAILURE,
          payload: 'Session expired. Please log in again.',
        });
      }
    }, refreshTime);

    return timer;
  };

  const _completeLogin = async (loginCall) => {
    dispatch({ type: AuthActions.SET_LOADING, payload: true });

    try {
      const loginResponse = await loginCall();
      const user = await authService.getCurrentUser();

      // Clear any existing dashboard cache on login to ensure fresh data for the new session
      DashboardCache.clearAllCache();

      // Populate orbit_users so ChangeUserPage can read staff from localStorage without an extra fetch
      try {
        const staffResponse = await staffAPI.getDepartmentStaff({ include_inactive: false });
        const availableUsers = staffResponse.data?.staff_members || [];
        const departmentName = user.department?.name || user.department_name || '';
        localStorage.setItem('orbit_users', JSON.stringify({
          current_user: user,
          available_users: availableUsers,
          original_department: departmentName,
        }));
      } catch {
        // Non-critical — ChangeUserPage will redirect to /login if the key is missing
      }

      // Refresh the PIN-login gate token separately so a staff-fetch failure
      // doesn't prevent PIN login from working on the Switch User page.
      // (For password login the /login response already stored a token; this
      // call keeps it fresh and aligned with the staff-list cache timing.)
      try {
        await authService.refreshSwitchSession();
      } catch {
        // Non-critical
      }

      dispatch({
        type: AuthActions.LOGIN_SUCCESS,
        payload: {
          user,
          token: loginResponse.token,
        },
      });

      return { success: true, user };
    } catch (error) {
      console.error('AuthContext: Login failed:', error);
      dispatch({
        type: AuthActions.LOGIN_FAILURE,
        payload: error.message,
      });

      return { success: false, error: error.message, code: error.code };
    }
  };

  const login = async (credentials) => {
    console.log('AuthContext: Attempting login');
    const result = await _completeLogin(() => authService.login(credentials));
    if (result.success) console.log('AuthContext: Login successful');
    return result;
  };

  const loginWithPin = async (credentials) => {
    console.log('AuthContext: Attempting PIN login');
    const result = await _completeLogin(() => authService.loginWithPin(credentials));
    if (result.success) console.log('AuthContext: PIN login successful');
    return result;
  };

  /**
   * Take over a login made elsewhere (e.g. a staff PIN login on a scanner
   * phone): loginCall must store the tokens and resolve to { token }. Unlike
   * login(), it skips the Switch User staff-list cache, which staff without
   * staff:read would get a 403 for.
   */
  const adoptLogin = async (loginCall) => {
    try {
      const loginResponse = await loginCall();
      const user = await authService.getCurrentUser();
      DashboardCache.clearAllCache();
      dispatch({ type: AuthActions.LOGIN_SUCCESS, payload: { user, token: loginResponse.token } });
      return { success: true, user };
    } catch (error) {
      return { success: false, error: error.message, code: error.code };
    }
  };

  const logout = async () => {
    try {
      console.log('AuthContext: Logging out');
      await authService.logout();
      // Deliberately NOT clearing the switch-session gate token here: this logout()
      // is what ChangeUserPage calls before showing the user grid, and the gate must
      // survive that so PIN login keeps working. It's only cleared by an explicit
      // "back to login" exit (see ChangeUserPage.handleBackToLogin).
    } catch (error) {
      console.error('AuthContext: Logout error:', error);
    } finally {
      // Clear dashboard cache on logout to prevent data leakage between users
      try {
        DashboardCache.clearAllCache();
        console.log('AuthContext: Dashboard cache cleared on logout');
      } catch (cacheError) {
        console.warn('AuthContext: Failed to clear dashboard cache on logout:', cacheError);
      }

      dispatch({ type: AuthActions.LOGOUT });
      console.log('AuthContext: Logout complete');
    }
  };

  const refreshToken = async () => {
    try {
      console.log('AuthContext: Manual token refresh requested');
      const refreshResult = await authService.refreshToken();
      
      if (refreshResult && refreshResult.token) {
        dispatch({
          type: AuthActions.TOKEN_REFRESH_SUCCESS,
          payload: { token: refreshResult.token },
        });
        console.log('AuthContext: Manual token refresh successful');
        return true;
      } else {
        throw new Error('Token refresh returned no token');
      }
    } catch (error) {
      console.error('AuthContext: Manual token refresh failed:', error);
      dispatch({
        type: AuthActions.TOKEN_REFRESH_FAILURE,
        payload: 'Failed to refresh session. Please log in again.',
      });
      return false;
    }
  };

  const clearError = () => {
    dispatch({ type: AuthActions.CLEAR_ERROR });
  };

  const hasScope = (scope) => {
    return tokenService.hasScope(scope);
  };

  const hasAnyScope = (scopes) => {
    return tokenService.hasAnyScope(scopes);
  };

  const value = {
    ...state,
    login,
    loginWithPin,
    adoptLogin,
    logout,
    refreshToken, // Add refreshToken to context value
    clearError,
    hasScope,
    hasAnyScope,
    hasSwitchSession: authService.hasSwitchSession.bind(authService),
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

// Custom hook to use auth context
export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}